import React, { useState, useRef, useEffect } from "react";
import { Search, X, ArrowRight, Tag, Clock, History, Trash2 } from "lucide-react";
import { ProcessItem } from "@/types";

interface SearchBarProps {
  searchTerm: string;
  searchResults: ProcessItem[];
  onSearchChange: (term: string) => void;
  onClear: () => void;
  onResultSelect: (item: ProcessItem, focusType?: 'document' | 'participant' | 'description') => void;
  /**
   * Gör det möjligt att skicka in egna CSS-klasser till sökbarens container
   */
  className?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchTerm,
  searchResults,
  onSearchChange,
  onClear,
  onResultSelect,
  className, // Lägg till className här
}) => {
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<
    'all' | 'process' | 'document' | 'participant' | 'description'
  >('all');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showRecent, setShowRecent] = useState(false);
  const [showRecentHistory, setShowRecentHistory] = useState(false);

  // Hämta tidigare sökningar från localStorage
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches).slice(0, 5));
      } catch (e) {
        console.error('Failed to parse recent searches:', e);
      }
    }
  }, []);

  // Stäng sökrutan och historiken när man klickar utanför
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current && 
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowRecentHistory(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Spara sökningar till localStorage
  const saveSearch = (term: string) => {
    if (!term.trim()) return;
    
    const updatedSearches = [
      term, 
      ...recentSearches.filter(s => s !== term)
    ].slice(0, 5);
    
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  // Använd en tidigare sökning
  const useRecentSearch = (term: string) => {
    onSearchChange(term);
    inputRef.current?.focus();
    setShowRecentHistory(false);
  };

  // Ta bort en sökning från historiken
  const removeSearchFromHistory = (term: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Förhindra att hela raden klickas
    const updatedSearches = recentSearches.filter(s => s !== term);
    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  // Rensa all sökhistorik
  const clearSearchHistory = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  // Hantera val av resultat med fokus på specifik typ
  const handleResultSelect = (
    item: ProcessItem,
    focusType?: 'document' | 'participant' | 'description'
  ) => {
    saveSearch(searchTerm);
    onResultSelect(item, focusType);
    setShowRecent(false);
  };

  // Filtrera resultat baserat på aktiv flik
  const filteredResults = searchResults.filter(result => {
    if (activeTab === 'all') return true;
    if (activeTab === 'process') return true;
    if (activeTab === 'document') return result.field_4 && result.field_4.length > 0;
    if (activeTab === 'participant') {
      return (
        (result.Deltagareochansvar1 && result.Deltagareochansvar1.length > 0) || 
        (result.Deltagareochansvar2 && result.Deltagareochansvar2.length > 0) || 
        (result.field_5 && result.field_5.length > 0)
      );
    }
    if (activeTab === 'description') return result.field_3 && result.field_3.length > 0;
    return true;
  });

  // Räkna antalet resultat per kategori
  const resultCounts = {
    all: searchResults.length,
    process: searchResults.filter(r => r).length,
    document: searchResults.filter(r => r.field_4 && r.field_4.length > 0).length,
    participant: searchResults.filter(r => 
      (r.Deltagareochansvar1 && r.Deltagareochansvar1.length > 0) ||
      (r.Deltagareochansvar2 && r.Deltagareochansvar2.length > 0) ||
      (r.field_5 && r.field_5.length > 0)
    ).length,
    description: searchResults.filter(r => r.field_3 && r.field_3.length > 0).length,
  };

  // Highlighta matchande text
  const highlightMatch = (text: string, term: string) => {
    if (!term || !text) return text;
    
    try {
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return text.replace(
        regex, 
        '<mark class="bg-orange-300/30 text-white px-1 rounded">$1</mark>'
      );
    } catch (e) {
      return text;
    }
  };

  // Kontrollera om sökningen matchar något i deltagaransvaret
  const hasParticipantResponsibilityMatch = (item: ProcessItem, term: string) => {
    if (!term) return false;
    return (
      (item.Deltagareochansvar1 && 
        item.Deltagareochansvar1.toLowerCase().includes(term.toLowerCase())) ||
      (item.Deltagareochansvar2 && 
        item.Deltagareochansvar2.toLowerCase().includes(term.toLowerCase()))
    );
  };

  return (
    <div 
      ref={searchContainerRef} 
      className={`relative w-full md:w-[32rem] lg:w-[36rem] max-w-full ${className ?? ''}`}
    >
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setShowRecent(true)}
          placeholder="Sök process, moment, dokument..."
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg py-2 pl-10 pr-10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300/50"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        
        {searchTerm ? (
          <button
            onClick={onClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        ) : recentSearches.length > 0 ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowRecentHistory(!showRecentHistory);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            type="button"
          >
            <History className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Sökresultat */}
      {searchTerm && searchResults.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800/95 rounded-lg shadow-xl border border-gray-700 max-h-96 overflow-hidden backdrop-blur-xl z-50">
          {/* Fliknavigation */}
          <div className="flex border-b border-gray-700/50 p-2 gap-1 overflow-x-auto no-scrollbar">
            <button 
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                activeTab === 'all' 
                  ? 'bg-orange-300 text-gray-900' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
              type="button"
            >
              Alla ({resultCounts.all})
            </button>
            <button 
              onClick={() => setActiveTab('process')}
              className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                activeTab === 'process' 
                  ? 'bg-orange-300 text-gray-900' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
              type="button"
            >
              Processer ({resultCounts.process})
            </button>
            <button 
              onClick={() => setActiveTab('document')}
              className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                activeTab === 'document' 
                  ? 'bg-orange-300 text-gray-900' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
              type="button"
            >
              Dokument ({resultCounts.document})
            </button>
            <button 
              onClick={() => setActiveTab('participant')}
              className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                activeTab === 'participant' 
                  ? 'bg-orange-300 text-gray-900' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
              type="button"
            >
              Roller ({resultCounts.participant})
            </button>
            <button 
              onClick={() => setActiveTab('description')}
              className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                activeTab === 'description' 
                  ? 'bg-orange-300 text-gray-900' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
              type="button"
            >
              Beskrivningar ({resultCounts.description})
            </button>
          </div>

          {/* Resultatlista */}
          <div className="max-h-80 overflow-y-auto custom-scrollbar">
            {filteredResults.map((result, index) => (
              <div
                key={index}
                onClick={() => handleResultSelect(result)}
                className="w-full text-left p-4 hover:bg-gray-700/50 border-b border-gray-700/30 last:border-b-0 cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-1">
                  {result.hierarchicalNumber && (
                    <span className="bg-gray-800 px-2 py-1 rounded font-mono text-xs text-orange-300">
                      {result.hierarchicalNumber}
                    </span>
                  )}
                  <span className="text-orange-300 font-medium">
                    <span 
                      dangerouslySetInnerHTML={{ 
                        __html: highlightMatch(result.Title, searchTerm) 
                      }} 
                    /> 
                    <ArrowRight className="inline-block h-3 w-3 mx-1" /> 
                    <span 
                      dangerouslySetInnerHTML={{ 
                        __html: highlightMatch(result.field_0, searchTerm) 
                      }} 
                    /> 
                    <ArrowRight className="inline-block h-3 w-3 mx-1" /> 
                    <span 
                      dangerouslySetInnerHTML={{ 
                        __html: highlightMatch(result.field_2, searchTerm) 
                      }} 
                    />
                  </span>
                </div>
                
                {/* Innehållsspecifika länkar/badges */}
                <div className="flex flex-wrap gap-2 mt-2 ml-14">
                  {/* Beskrivning */}
                  {result.field_3 && (
                    <div className="flex-1 min-w-0">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResultSelect(result, 'description');
                        }}
                        className="inline-flex items-center gap-1 text-xs rounded-full bg-orange-500/20 text-orange-300 px-2 py-0.5 hover:bg-orange-500/30"
                        type="button"
                      >
                        Beskrivning
                      </button>
                      <p className="text-gray-400 text-xs mt-1 line-clamp-1">
                        <span 
                          dangerouslySetInnerHTML={{ 
                            __html: highlightMatch(result.field_3, searchTerm) 
                          }} 
                        />
                      </p>
                    </div>
                  )}
                  
                  {/* Dokument */}
                  {result.field_4 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResultSelect(result, 'document');
                      }}
                      className="inline-flex items-center gap-1 text-xs rounded-full bg-blue-500/20 text-blue-300 px-2 py-0.5 hover:bg-blue-500/30"
                      type="button"
                    >
                      <Tag className="h-3 w-3" /> Dokument
                    </button>
                  )}
                  
                  {/* Deltagare och ansvar */}
                  {(
                    (result.field_5 && result.field_5.length > 0) || 
                    (result.Deltagareochansvar1 && result.Deltagareochansvar1.length > 0) ||
                    (result.Deltagareochansvar2 && result.Deltagareochansvar2.length > 0)
                  ) && (
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); 
                          handleResultSelect(result, 'participant');
                        }}
                        className="inline-flex items-center gap-1 text-xs rounded-full bg-green-500/20 text-green-300 px-2 py-0.5 hover:bg-green-500/30"
                        type="button"
                      >
                        <Tag className="h-3 w-3" /> Roll
                      </button>
                      {/* Extra tag om matchning i ansvarsfälten */}
                      {hasParticipantResponsibilityMatch(result, searchTerm) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResultSelect(result, 'participant');
                          }}
                          className="inline-flex items-center gap-1 text-xs rounded-full bg-purple-500/20 text-purple-300 px-2 py-0.5 hover:bg-purple-500/30"
                          type="button"
                        >
                          <Tag className="h-3 w-3" /> Ansvar i moment
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {filteredResults.length === 0 && (
              <div className="p-6 text-center text-gray-400">
                Inga resultat i kategorin {activeTab}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sökhistorik dropdown */}
      {showRecentHistory && recentSearches.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800/95 rounded-lg shadow-xl border border-gray-700 max-h-96 overflow-hidden backdrop-blur-xl z-50">
          <div className="flex items-center justify-between p-2 border-b border-gray-700/50">
            <div className="flex items-center text-gray-400 text-xs">
              <History className="h-3 w-3 mr-1" />
              <span>TIDIGARE SÖKNINGAR</span>
            </div>
            <button
              onClick={clearSearchHistory}
              className="text-xs text-gray-400 hover:text-white flex items-center"
              type="button"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Rensa alla
            </button>
          </div>
          {recentSearches.map((term, index) => (
            <div
              key={index}
              className="flex items-center text-left p-2 hover:bg-gray-700/50 group"
            >
              <div
                onClick={() => useRecentSearch(term)}
                className="flex-1 flex items-center text-sm text-gray-300 cursor-pointer"
              >
                <Clock className="h-3 w-3 mr-2 text-gray-500" />
                {term}
              </div>
              <button
                onClick={(e) => removeSearchFromHistory(term, e)}
                className="text-gray-500 hover:text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                type="button"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
